import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdCameraAlt, MdOutlineSmartphone } from 'react-icons/md'
import { Popover, Spin, Tag } from 'antd'
import dayjs from 'dayjs'
import Api from 'services/api'
import { BuildingSessionDevice } from 'types/building'

type Props = {
  buildingId: number | string
  statusText?: string
  className?: string
}

export default function ShootingStatusBadge({
  buildingId,
  statusText,
  className
}: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [devices, setDevices] = useState<BuildingSessionDevice[]>([])
  const [fetched, setFetched] = useState<boolean>(false)

  const fetchDevices = async () => {
    if (loading) return
    setLoading(true)
    try {
      const data = await Api.get<BuildingSessionDevice[]>(
        `/v1/building/${buildingId}/session/devices`
      )
      setDevices(data || [])
      setFetched(true)
    } catch {
      setDevices([])
    } finally {
      setLoading(false)
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (newOpen && !fetched) {
      fetchDevices()
    }
  }

  const renderContentBody = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-4">
          <Spin size="small" />
        </div>
      )
    }

    if (devices.length === 0) {
      return (
        <div className="text-xs text-gray-500 py-3 text-center">
          {t('building.noShootingDevices')}
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-2 max-h-[220px] overflow-y-auto">
        {devices.map((device) => {
          let userName = '-'
          if (device.user) {
            const lastName = device.user.lastName || ''
            const firstName = device.user.firstName || ''
            const fullName = `${lastName} ${firstName}`.trim()
            if (fullName) {
              userName = fullName
            }
          }

          return (
            <div
              key={device.id}
              className="bg-gray-50 hover:bg-gray-100 p-2 rounded text-xs border border-gray-100 transition-colors"
            >
              <div className="flex items-center gap-1.5 font-medium text-gray-800 mb-1">
                <MdOutlineSmartphone className="text-gray-500 text-sm flex-shrink-0" />
                <span
                  className="truncate"
                  title={device.deviceName || device.deviceId}
                >
                  {device.deviceName || device.deviceId}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-1 text-gray-600 text-[11px] mt-1 pl-4">
                <div>
                  <span className="text-gray-400">
                    {t('building.shootingUser')}:{' '}
                  </span>
                  <span className="font-medium text-gray-700">{userName}</span>
                </div>
                <div>
                  <span className="text-gray-400">
                    {t('building.startedAt')}:{' '}
                  </span>
                  <span>
                    {device.startedAt
                      ? dayjs(device.startedAt).format('MM/DD HH:mm')
                      : '-'}
                  </span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const popoverContent = (
    <div
      className="w-[280px] p-1"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between border-b pb-2 mb-2">
        <div className="flex items-center gap-1.5 font-semibold text-gray-800 text-sm">
          <MdCameraAlt className="text-orange-500 text-base" />
          <span>{t('building.shootingDevices')}</span>
        </div>
        {devices.length > 0 && (
          <span className="bg-orange-100 text-orange-600 text-xs px-2 py-0.5 rounded-full font-medium">
            {devices.length}
          </span>
        )}
      </div>
      {renderContentBody()}
    </div>
  )

  return (
    <div
      onClick={(e) => {
        e.stopPropagation()
      }}
      className={`inline-block ${className || ''}`}
    >
      <Popover
        content={popoverContent}
        trigger={['hover', 'click']}
        open={open}
        onOpenChange={handleOpenChange}
        placement="bottomLeft"
      >
        <span className="cursor-pointer inline-flex items-center gap-1">
          <Tag
            color="orange"
            className="!m-0 !px-2 !py-0.5 flex items-center gap-1 text-xs font-medium cursor-pointer hover:opacity-85 shadow-sm"
          >
            <MdCameraAlt className="text-sm" />
            <span>{statusText || t('building.40')}</span>
          </Tag>
        </span>
      </Popover>
    </div>
  )
}

ShootingStatusBadge.defaultProps = {
  statusText: '',
  className: ''
}
